import DOMPurify from "dompurify";
import { generateHTML } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import type { Post } from '@/types';

/**
 * Check if content appears to be TipTap JSON ({"type":"doc",...})
 */
function isTipTapJson(text: string): boolean {
  if (!text.startsWith("{")) return false;
  try {
    const parsed = JSON.parse(text);
    return parsed?.type === "doc" && Array.isArray(parsed?.content);
  } catch {
    return false;
  }
}

/**
 * TipTap extensions used for generateHTML (matches the editor config)
 */
const tiptapExtensions = [
  StarterKit.configure({ heading: false, codeBlock: false }),
  Underline,
  Link.configure({ openOnClick: false }),
];

/**
 * Convert TipTap JSON string to HTML using the same extensions as the editor
 */
function tiptapJsonToHtml(text: string): string {
  try {
    const json = JSON.parse(text);
    return generateHTML(json, tiptapExtensions);
  } catch {
    return text;
  }
}

/**
 * Check if content appears to be HTML (from rich text editor)
 */
function isHtmlContent(text: string): boolean {
  // Check for common TipTap/HTML tags
  return /<(p|div|span|strong|em|u|s|a|ul|ol|li|blockquote|br)\b[^>]*>/i.test(text);
}

/**
 * Convert TipTap mention spans to clickable links
 * Input: <span data-type="mention" class="mention" data-id="ULID" data-label="username">@username</span>
 * Output: <a href="/profile/username" class="mention">@username</a>
 */
function convertMentionSpansToLinks(html: string): string {
  return html.replace(
    /<span[^>]*data-type="mention"[^>]*data-label="([^"]*)"[^>]*>([^<]*)<\/span>/gi,
    '<a href="/profile/$1" class="mention">$2</a>'
  );
}

/**
 * Convert TipTap song reference spans to clickable links
 * Input: <span data-type="songReference" class="song-reference" data-id="ULID" data-title="Title">#Title</span>
 * Output: <a href="/song/ULID" class="song-reference">#Title</a>
 */
function convertSongReferenceSpansToLinks(html: string): string {
  return html.replace(
    /<span[^>]*data-type="songReference"[^>]*data-id="([^"]*)"[^>]*>([^<]*)<\/span>/gi,
    '<a href="/song/$1" class="song-reference">$2</a>'
  );
}

/**
 * Format hashtags and mentions in text
 */
function formatHashtagsAndMentions(text: string, mentionClass: string, hashtagClass: string): string {
  let formatted = text;

  // Replace @mentions with links
  formatted = formatted.replace(
    /@([a-zA-Z0-9_]+)/g,
    `<a href="/profile/$1" class="${mentionClass}">@$1</a>`
  );

  // Replace #hashtag[song:ULID] with links to song pages
  formatted = formatted.replace(
    /#([\w-]+)\[song:([a-zA-Z0-9]+)\]/g,
    `<a href="/song/$2" class="${hashtagClass}">#$1</a>`
  );

  // Replace regular #hashtags with search links (including hyphens which are common in hashtags)
  // Use negative lookbehind (?<!>) to avoid matching hashtags already inside anchor tags from song reference replacement
  formatted = formatted.replace(
    /(?<!>)#([\w-]+)/g,
    `<a href="/search?q=%23$1" class="${hashtagClass}">#$1</a>`
  );

  return formatted;
}

/**
 * Format post body text with clickable @mentions and #hashtags
 * For plain text: escapes HTML to prevent XSS and converts mentions/hashtags to links
 * For HTML content (from rich text editor): sanitizes with DOMPurify and converts TipTap nodes to links
 * - @username -> link to /profile/username
 * - #hashtag[song:ULID] -> link to /song/ULID (song reference)
 * - #hashtag -> link to /search?q=%23hashtag (regular hashtag)
 */
export function formatBodyWithLinks(
  text: string,
  mentionClass = "mention",
  hashtagClass = "hashtag"
): string {
  if (!text) return "";

  // If content is TipTap JSON, convert to HTML first
  if (isTipTapJson(text)) {
    text = tiptapJsonToHtml(text);
  }

  // If content is HTML (from rich text editor or converted from TipTap JSON), sanitize and convert nodes to links
  if (isHtmlContent(text)) {
    // Sanitize HTML to prevent XSS
    let sanitized = DOMPurify.sanitize(text, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'a', 'span', 'ul', 'ol', 'li', 'blockquote'],
      ALLOWED_ATTR: ['href', 'class', 'data-type', 'data-id', 'data-label', 'data-title', 'target', 'rel'],
    });

    sanitized = formatHashtagsAndMentions(sanitized, mentionClass, hashtagClass);

    // Convert TipTap mention/song-reference spans to clickable links
    sanitized = convertMentionSpansToLinks(sanitized);
    sanitized = convertSongReferenceSpansToLinks(sanitized);

    return sanitized;
  }

  // For plain text, escape HTML to prevent XSS
  let formatted = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\\n/g, "\n");

  formatted = formatHashtagsAndMentions(formatted, mentionClass, hashtagClass);

  return formatted;
}

/**
 * Extract plain text from post body (strips HTML/TipTap formatting)
 * Useful for previews, truncation, etc.
 */
export function getPlainTextFromBody(text: string): string {
  if (!text) return "";

  // If content is TipTap JSON, convert to HTML first
  if (isTipTapJson(text)) {
    text = tiptapJsonToHtml(text);
  }

  // If it's HTML, strip tags
  if (isHtmlContent(text)) {
    // Create a temporary element to extract text content
    const temp = document.createElement("div");
    temp.innerHTML = DOMPurify.sanitize(text);
    return temp.textContent || temp.innerText || "";
  }

  // Plain text - just handle escaped newlines
  return text.replace(/\\n/g, "\n");
}

/** Extract user info from a Post (comes via API's dynamic fields). */
export function getPostUser(post: Post): { name: string; avatar: string; username: string } {
  const u = post.user as Record<string, unknown> | undefined;
  return {
    name: (u?.displayName as string) || (u?.name as string) || (u?.username as string) || 'Unknown',
    avatar: (u?.avatarUrl as string) || (u?.avatar as string) || '',
    username: (u?.username as string) || '',
  };
}

/** Get the first image URL from a post. */
export function getPostImage(post: Post): string | undefined {
  const images = post.images as Array<Record<string, unknown>> | undefined;
  if (images && images.length > 0) {
    return (images[0]?.url as string) || (images[0] as unknown as string);
  }
  return undefined;
}

/** Get text content from a post (API may use body or content).
 *  Parses TipTap JSON and converts to plain text. */
export function getPostContent(post: Post): string {
  const raw = post.content || (post as Record<string, unknown>).body as string || '';
  return getPlainTextFromBody(raw);
}

/** Get all image URLs from a post. */
export function getPostImages(post: Post): string[] {
  const images = post.images as Array<Record<string, unknown>> | undefined;
  if (!images) return [];
  return images.map((img) => (typeof img === 'string' ? img : (img?.url as string) || '')).filter(Boolean);
}